import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, Play, Pause, ShieldCheck, FileText, ChevronLeft } from "lucide-react";
import { Toolbar } from "./AdminLayout";
import InterviewRecorder from "../components/InterviewRecorder";
import { SAMPLE_INTERVIEWS } from "./interviewSamples";

export default function Interviews() {
  const navigate = useNavigate();
  const toStory = (text) => { sessionStorage.setItem("nl_prefill_notes", text); navigate("/admin/articles?new=1"); };
  const [rec, setRec] = useState(false);
  const [open, setOpen] = useState(null);
  if (open) return <InterviewDetail iv={open} onBack={() => setOpen(null)} onStory={toStory} />;
  return (
    <div className="mx-auto max-w-2xl" data-testid="interviews-page">
      <Toolbar title="My Interviews" subtitle="Record conversations with permission and get a typed transcript afterwards" />
      <span className="mb-4 inline-block rounded-full bg-slate-200 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600" data-testid="interviews-preview-tag">Preview · sample interviews</span>
      <button onClick={() => setRec(true)} data-testid="interviews-record-button" className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 text-left text-[var(--navy)] shadow-sm active:scale-[0.99]">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600"><Mic size={24} /></span>
        <div><p className="font-bold">Record an interview</p><p className="text-xs text-slate-500">Optional · confirm permission, tap record, put your phone on the table</p></div>
      </button>
      <div className="mt-5 divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {SAMPLE_INTERVIEWS.map((iv) => (
          <button key={iv.id} onClick={() => setOpen(iv)} data-testid={`interview-${iv.id}`} className="flex w-full items-center gap-3 p-4 text-left hover:bg-slate-50">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--gold)]/15 text-[var(--gold)]"><FileText size={18} /></span>
            <div className="min-w-0 flex-1"><p className="line-clamp-2 text-sm font-semibold text-[var(--navy)]">{iv.title}</p><p className="text-xs text-slate-400">{iv.date} · {iv.length} · Transcript ready</p></div>
          </button>
        ))}
      </div>
      <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-400"><ShieldCheck size={14} /> Only you and your editors can hear or read your interviews.</p>
      {rec && <InterviewRecorder onClose={() => setRec(false)} onUseTranscript={toStory} />}
    </div>
  );
}

function InterviewDetail({ iv, onBack, onStory }) {
  const [playing, setPlaying] = useState(false);
  const [active, setActive] = useState(0);
  return (
    <div className="mx-auto max-w-2xl" data-testid="interview-detail">
      <button onClick={onBack} data-testid="interview-back" className="mb-3 flex items-center gap-1 text-sm font-semibold text-slate-500"><ChevronLeft size={16} /> My interviews</button>
      <h2 className="text-xl font-bold text-[var(--navy)]">{iv.title}</h2>
      <p className="text-xs text-slate-400">{iv.date} · {iv.length} · Permission confirmed</p>
      <div className="sticky top-14 z-10 mt-4 flex items-center gap-3 rounded-2xl bg-[var(--navy)] p-3 text-white lg:top-16">
        <button onClick={() => setPlaying(!playing)} data-testid="interview-play" className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--gold)]">{playing ? <Pause size={18} /> : <Play size={18} />}</button>
        <div className="h-1.5 flex-1 rounded-full bg-white/20"><div className="h-full rounded-full bg-[var(--gold)]" style={{ width: `${((active + 1) / iv.lines.length) * 100}%` }} /></div>
        <span className="font-mono text-xs">{iv.lines[active].t} / {iv.length}</span>
      </div>
      <div className="mt-4 space-y-1">
        {iv.lines.map((l, i) => (
          <button key={i} onClick={() => setActive(i)} data-testid={`transcript-line-${i}`} className={`block w-full rounded-xl p-3 text-left text-sm ${active === i ? "bg-amber-50 ring-1 ring-[var(--gold)]/40" : "hover:bg-slate-50"}`}>
            <span className="mr-2 font-mono text-[11px] text-slate-400">{l.t}</span>
            <span className={`mr-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${l.who === "Reporter" ? "bg-slate-100 text-slate-600" : "bg-[var(--gold)]/15 text-[var(--gold)]"}`}>{l.who}</span>
            <span className="text-slate-700">{l.text}</span>
          </button>
        ))}
      </div>
      <button data-testid="interview-send-to-story" onClick={() => onStory(iv.lines.map((l) => `${l.who}: ${l.text}`).join(" "))} className="mb-24 mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--navy)] py-3 text-sm font-semibold text-white lg:mb-0"><FileText size={16} /> Start a story from this interview</button>
    </div>
  );
}
